import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import ReactLoading from 'react-loading';
import moment from 'moment';
import useFormState from '../../FormStateHelper';
import { validateForm } from '../../../selectors/defaultSelectors';
import Field from '../../fields/Field';
import FieldCheckbox from '../../fields/FieldCheckbox';
import FieldNumber from '../../fields/FieldNumber';
import FieldDatePicker from '../../fields/FieldDatePicker';
import FieldDropzone from '../../fields/FieldDropzone';
import FieldSelect from '../../fields/FieldSelect';
import LocalizedMessage from 'react-l10n';
import { getTranslation } from '../../../lang/main';
import { axiosStripeUploadImage } from '../../../actions/user/stripe';
import { capitalize } from '../../../selectors/converterSelector';

/**
 * A helper function to generate field based on the given params
 *
 * @param fieldType
 * @param fieldProps
 * @returns {*}
 */
const generateField = (fieldType, fieldProps) => {
    switch (fieldType.toLowerCase()) {
        case 'text':
            return <Field {...fieldProps} />;

        case 'date':
            return (
                <FieldDatePicker
                    {...fieldProps}
                    dateFormat='DD/MM/YYYY'
                    placeholder='dd/mm/yyyy'
                    value={
                        fieldProps.value
                            ? moment(fieldProps.value).format('DD/MM/YYYY')
                            : ''
                    }
                />
            );

        case 'number':
            return <FieldNumber {...fieldProps} parseIntDisable={true} />;

        case 'decimal':
            return (
                <FieldNumber
                    {...fieldProps}
                    allowDecimals={true}
                    parseIntDisable={true}
                />
            );

        case 'boolean':
            return <FieldCheckbox {...fieldProps} />;

        case 'options':
            return <FieldSelect {...fieldProps} />;

        case 'file':
            return (
                <FieldDropzone
                    {...fieldProps}
                    onFileUpload={axiosStripeUploadImage}
                    allowImages={true}
                    maxFiles={1}
                    maxSize={10485760}
                    alternativeExplanation={
                        <LocalizedMessage
                            id={'FILE_UPLOAD.DRAG_DOCUMENT_TO_UPLOAD'}
                        />
                    }
                />
            );
    }

    return false;
};

const runRedirectToStripe = () => {
    window.location.href = `${window.baseURL}connect/verify/`;
};

const runRedirectToLogs = () => {
    window.location.href = `${window.baseURL}account/payout-logs/`;
};


const runRedirectToPayFast = () => {
    window.open('https://www.payfast.co.za/registration',)

};

function PayFastConnectForm(props) {
    const {
        initialized,
        userInfo,
        handleFormSubmission,
        stripeFieldData,
        isLoading,
        requestPayFastMissingFields,
        countryCode,
        isCompleted,
        requireInternalDetails,
        redirectToStripe,
    } = props;
    const isFieldLoading = isLoading.basicInfo || false;
    const isStripeLoading = isLoading.stripeFields || false;
    const {
        formData,
        bindUpdateFormData,
        updateFormData,
        isSubmitting,
        submitForm,
    } = useFormState({
        handleFormSubmission: handleFormSubmission,
        initialFormState: {},
    });
    const [isValidated, setIsValidated] = useState(false);
    const [showBankDetails, toggleBankDetails] = useState(false);
    let fieldRefs = {};

    const finalFormData = {
        ...userInfo.basicInfo,
        ...formData,
    };

    const userStripeCountryCode = userInfo.basicInfo.StripeCountryCode || '';

    const onFormSubmit = (e) => {
        e.preventDefault();

        const submittedData = Object.keys(stripeFieldData).reduce(
            (list, fieldName) => {
                if (typeof finalFormData[fieldName] != 'undefined') {
                    list[fieldName] = finalFormData[fieldName];
                }

                return list;
            },
            {}
        );

        submitForm(submittedData, {
            success: (response) => {
                // Do something?
                console.log('Success response', response);
                toggleBankDetails(false);
                window.location.reload();
                // if (redirectToStripe) {
                    // runRedirectToStripe();
                // } else {
                //     toggleBankDetails(false);
                // }
            },
        });
    };

    const onFieldUpdate = (e) => {
        bindUpdateFormData(e);
    };

    const onDateFieldUpdate = (name, value) => {
        updateFormData(name, moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD'));
    };

    const generateFormFromJSON = (jsonData, config = {}) => {
        return Object.entries(jsonData)
            .sort(([, a], [, b]) => {
                const compareA = a.Order || '';
                const compareB = b.Order || '';

                return compareA > compareB ? 1 : compareB > compareA ? -1 : 0;
            })
            .reduce((list, [fieldName, fieldData]) => {
                const {
                    Extra = false,
                    Type,
                    Fulfilled,
                    Options = undefined,
                } = fieldData;

                const {
                    excludedGroupKeys = [],
                    includedGroupKeys = [],
                } = config;

                let isIncluded = true;
                if (
                    Array.isArray(includedGroupKeys) &&
                    includedGroupKeys.length > 0
                ) {
                    isIncluded = false;
                    includedGroupKeys.forEach((includedGroupKey) => {
                        if (fieldName.indexOf(includedGroupKey) !== -1) {
                            isIncluded = true;
                        }
                    });
                }

                if (
                    Array.isArray(excludedGroupKeys) &&
                    excludedGroupKeys.length > 0
                ) {
                    excludedGroupKeys.forEach((excludedGroupKey) => {
                        if (fieldName.indexOf(excludedGroupKey) !== -1) {
                            isIncluded = false;
                        }
                    });
                }

                if (!isIncluded) {
                    return list;
                }
                let finalTranslatedLabel = '';
                const translatedLabel = getTranslation(
                    `FIELD.PAYFAST.${fieldName}.${window.locale}`
                );
                finalTranslatedLabel =
                    translatedLabel !==
                    `FIELD.PAYFAST.${fieldName}.${window.locale}`
                        ? translatedLabel
                        : getTranslation(`FIELD.PAYFAST.${fieldName}`);
                const stripeToolTip = getTranslation(
                    `FIELD.PAYFAST.${fieldName}.StripeToolTip.${window.locale}`
                );
                const finalStripeToolTip =
                    stripeToolTip !==
                    `FIELD.PAYFAST.${fieldName}.StripeToolTip.${window.locale}`
                        ? stripeToolTip
                        : false;

                const fieldProps = {
                    key: `stripe-${fieldName}`,
                    ref: (r) => (fieldRefs[fieldName] = r),
                    label: finalTranslatedLabel,
                    animatedLabel: finalTranslatedLabel,
                    tooltip: finalStripeToolTip,
                    name: fieldName,
                    onChange:
                        Type == 'Date'
                            ? (date) => onDateFieldUpdate(fieldName, date)
                            : onFieldUpdate,
                    value: finalFormData[fieldName] || '',
                    isLoading: isFieldLoading,
                    onSuccessfulUpload: (response) => {
                        const { data } = response;
                        updateFormData(fieldName, data.ID);
                    },
                };

                if (Array.isArray(Options)) {
                    fieldProps.options = Options.map((opt) => {
                        return {
                            value: opt,
                            label: capitalize(opt),
                        };
                    });
                }

                const field = generateField(
                    window.localeCountry == 'United States' &&
                        fieldName.toString() == 'StripeBSB'
                        ? 'number'
                        : Type,
                    fieldProps
                );

                if (field !== false) {
                    list.push(field);
                }

                return list;
            }, []);
    };

    const otherFormFields = generateFormFromJSON(stripeFieldData, {
        excludedGroupKeys: [
            'StripeRepresentative_',
            'StripeDirector_',
            'StripeOwner_',
            'StripeCompany_',
            'StripeBusinessProfile_',
        ],
    });
    const businessProfileFormFields = generateFormFromJSON(stripeFieldData, {
        includedGroupKeys: ['StripeBusinessProfile_'],
    });
    const companyFormFields = generateFormFromJSON(stripeFieldData, {
        includedGroupKeys: ['StripeCompany_'],
    });
    const representativeFormFields = generateFormFromJSON(stripeFieldData, {
        includedGroupKeys: ['StripeRepresentative_'],
    });
    const directorFormFields = generateFormFromJSON(stripeFieldData, {
        includedGroupKeys: ['StripeDirector_'],
    });
    const ownerFormFields = generateFormFromJSON(stripeFieldData, {
        includedGroupKeys: ['StripeOwner_'],
    });

    let isEmptyFormFields =
        otherFormFields.length === 0 &&
        businessProfileFormFields.length === 0 &&
        companyFormFields.length === 0 &&
        representativeFormFields.length === 0 &&
        directorFormFields.length === 0 &&
        ownerFormFields.length === 0;

    // Effects
    // --------------------
    // - Request fields
    // useEffect(() => {
    //     if (userStripeCountryCode != '') {
    //         requestPayFastMissingFields();
    //     }
    // }, [userStripeCountryCode]);

    useEffect(() => {
            requestPayFastMissingFields();
    }, []);

    // - Validate form
    useEffect(() => {
        setIsValidated(!validateForm(fieldRefs));
    }, [finalFormData]);

    // if(initialized && !isFieldLoading && !isStripeLoading && isEmptyFormFields) {
    //     return (
    //         <div className='card card--border card--form card--stackable' id='required-stripe-fields'>
    //             <div className="text-box">
    //                 <p><LocalizedMessage id={'STRIPE_CONNECT_FORM.EMPTY_MESSAGE'} /></p>
    //             </div>
    //         </div>
    //     );
    // }

    let formFields = (
        <form className='form' onSubmit={onFormSubmit}>
            <div className='row'>
                <div className='col-xs-12 col-md-7 vspace vspace--small'>
                    {otherFormFields.length > 0 && (
                        <div className='vspace--small'>{otherFormFields}</div>
                    )}
                      {/* {ownerFormFields.length > 0 && (
                        <div className="vspace">
                            <span className="heading heading--lv5"><LocalizedMessage id={'STRIPE_CONNECT_FORM.OWNER.HEADING'}/></span>
                            {ownerFormFields}
                        </div>
                    )} */}

                    <button
                        type='submit'
                        className={`form__submit button ${
                            !isValidated ? 'button--disabled' : ''
                        }`}
                        disabled={!isValidated}
                    >
                        <span
                            style={{
                                opacity: isSubmitting === true ? 0 : 1,
                            }}
                        >
                             <LocalizedMessage id={'PAYFAST_SAVE__DETAILS'} />
                        </span>
                        {isSubmitting === true && (
                            <ReactLoading
                                className='button__spinner'
                                type='spin'
                                color='white'
                                height={20}
                                width={20}
                            />
                        )}
                    </button>
                    <button
                        type='button'
                        className='button button--border'
                        onClick={(e) => runRedirectToPayFast()}
                        style={{ background: 'white', color:'#00ade5', textTransform: 'uppercase' }}
                    >
                        <span>
                            <LocalizedMessage id={'CREATE_NEW'} />
                        </span>
                    </button>
                </div>
            </div>
        </form>
    );

    const renderBody = () => {
        if (window.isPayFastComplete == 'true') {
            return (
                <>
                    <h3 className='heading heading--lv4'>
                        <LocalizedMessage
                            id={'STRIPE_CONNECT_FORM.SUCCESS.HEADING'}
                        />
                    </h3>
                    <div className='vspace vspace--small'>
                        <div className='text-box'>
                            <p>
                                <LocalizedMessage
                                    id={'PAYFAST_CONNECT_FORM.SUCCESS.CONTENT'}
                                />
                            </p>
                        </div>
                    </div>
                    {/* <button
                        type='button'
                        className='button'
                        onClick={(e) => toggleBankDetails(true)}
                    >
                        <span>
                            <LocalizedMessage id={'EDIT_BANK_DETAILS'} />
                        </span>
                    </button> */}
                    {/* <button
                        type='button'
                        className='button button--border'
                        onClick={(e) => runRedirectToStripe()}
                        style={{ textTransform: 'uppercase' }}
                    >
                        <span>
                            <LocalizedMessage
                                id={'EDIT_STRIPE_CONFIGURATION'}
                            />
                        </span>
                    </button> */}
                    <button
                        type='button'
                        className='button button--border'
                        onClick={(e) => runRedirectToLogs()}
                        style={{ textTransform: 'uppercase' }}
                    >
                        <span>
                            <LocalizedMessage id={'VIEW_LOGS'} />
                        </span>
                    </button>
                </>
            );
        } else {
            return (
                <>
                    <h3 className='heading heading--lv4'>
                        <LocalizedMessage
                            id={'PAYFAST_CONNECT_FORM.WARNING.HEADING'}
                        />
                    </h3>
                    <h4 className='heading heading--lv5'>
                        {/*<LocalizedMessage id={'STRIPE_CONNECT_FORM.WARNING.SUBHEADING'} countryCode={countryCode.toUpperCase()} />*/}
                    </h4>
                    <div className='vspace vspace--small'>
                        <div className='text-box'>
                            <p>
                                <LocalizedMessage
                                    id={'PAYFAST_CONNECT_FORM.WARNING.CONTENT'}
                                />
                            </p>
                        </div>
                    </div>
                    {formFields}
                </>
            );
        }
    };

    return (
        <React.Fragment>
            <div
                className={classnames(
                    'boxed-message',
                    window.isPayFastComplete == 'false'
                        ? 'boxed-message--yellow'
                        : 'boxed-message--clean'
                )}
            >
                <div className='boxed-message__header'>
                    <span className='boxed-message__header-icon__holder'>
                        <span className='boxed-message__header-icon'>
                            <span
                                className={classnames(
                                    'icon',
                                    window.isPayFastComplete == 'false'
                                        ? 'icon-warning'
                                        : 'icon-check-thick icon--green'
                                )}
                            ></span>
                        </span>
                    </span>
                    <div className='row'>
                        <div className='col-xs-12 col-md-8'>{renderBody()}</div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default PayFastConnectForm;
